/**
 * Wrappers tipados sobre os endpoints REST de saga (SPEC-20260503-0521).
 * Usa `apiFetch<T>` do bootstrap (Bearer JWT + dedup automático para GETs).
 */

import { apiFetch } from "./auth";
import type {
  ListSagasParams,
  ListSagasResult,
  SagaRunResult,
  TriggerSagaInput,
  TriggerSagaResult,
} from "../types/saga";

interface ApiSuccess<T> { success: true; data: T }
interface ApiPaginated<T> { success: true; data: T[]; pagination: { limit: number; offset: number; total: number } }

export async function triggerSaga(input: TriggerSagaInput): Promise<TriggerSagaResult> {
  const body = await apiFetch<ApiSuccess<TriggerSagaResult>>("/api/sagas", {
    method: "POST",
    body: JSON.stringify({
      sagaType: input.sagaType,
      input: input.input,
      idempotencyKey: input.idempotencyKey,
    }),
    headers: { "Idempotency-Key": input.idempotencyKey },
  });
  return body.data;
}

export async function getSaga(id: string): Promise<SagaRunResult> {
  const body = await apiFetch<ApiSuccess<SagaRunResult>>(`/api/sagas/${id}`);
  return body.data;
}

export async function listSagas(params: ListSagasParams = {}): Promise<ListSagasResult> {
  const search = new URLSearchParams();
  if (params.sagaType) search.set("sagaType", params.sagaType);
  if (params.status) search.set("status", params.status);
  if (params.limit !== undefined) search.set("limit", String(params.limit));
  if (params.offset !== undefined) search.set("offset", String(params.offset));
  const qs = search.toString();
  const path = qs ? `/api/sagas?${qs}` : "/api/sagas";
  const body = await apiFetch<ApiPaginated<SagaRunResult>>(path);
  return { data: body.data, pagination: body.pagination };
}

export async function cancelSaga(id: string, reason?: string): Promise<SagaRunResult> {
  const body = await apiFetch<ApiSuccess<SagaRunResult>>(`/api/sagas/${id}/cancel`, {
    method: "POST",
    body: JSON.stringify(reason ? { reason } : {}),
  });
  return body.data;
}

/**
 * Gera idempotency-key default: timestamp + random.
 * Suficiente para o MVP — UUID seria melhor, mas evita dep de uuid no client.
 */
export function generateIdempotencyKey(): string {
  return `ui-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
